'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useSSOStore } from '@/lib/sso/store';
import type { SSOConfig, SAMLConfigInput } from '@/lib/sso/types';
import { Download, Copy, Check } from 'lucide-react';
import { getSAMLMetadata } from '@/lib/sso/api';

interface SAMLConfigFormProps {
  config: SSOConfig | null;
  onCancel?: () => void;
}

const NAME_ID_FORMATS = [
  { value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress', label: 'Email Address' },
  { value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent', label: 'Persistent' },
  { value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient', label: 'Transient' },
  { value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified', label: 'Unspecified' },
];

export function SAMLConfigForm({ config, onCancel }: SAMLConfigFormProps) {
  const { saveSSOConfig, ssoLoading } = useSSOStore();
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<SAMLConfigInput>({
    defaultValues: {
      provider: 'saml',
      display_name: config?.display_name || '',
      saml_sso_url: config?.saml_sso_url || '',
      saml_slo_url: config?.saml_slo_url || '',
      saml_certificate: config?.saml_certificate || '',
      saml_name_id_format: config?.saml_name_id_format || 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
      saml_attribute_mapping: config?.saml_attribute_mapping || {
        email: 'email',
        firstName: 'firstName',
        lastName: 'lastName',
      },
      allowed_domains: config?.allowed_domains || [],
      auto_provision_users: config?.auto_provision_users ?? true,
      default_role: config?.default_role || 'member',
      jit_provisioning: config?.jit_provisioning ?? true,
    },
  });

  const onSubmit = async (data: SAMLConfigInput) => {
    try {
      await saveSSOConfig(data);
    } catch (error) {
      // Error handled by store
    }
  };

  const handleDownloadMetadata = async () => {
    try {
      const metadata = await getSAMLMetadata();
      const blob = new Blob([metadata], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sp-metadata.xml';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download metadata:', error);
    }
  };

  const handleCopyEntityId = () => {
    navigator.clipboard.writeText(config?.saml_entity_id || `${window.location.origin}/api/v1/sso/saml/metadata`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* SP Information (Read-only) */}
      {config && (
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-sm">Service Provider (SP) Information</h4>
          <p className="text-sm text-muted-foreground">
            Provide these values to your identity provider
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Entity ID (Issuer)</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCopyEntityId}
              >
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
            <Input
              value={config.saml_entity_id || `${window.location.origin}/api/v1/sso/saml/metadata`}
              readOnly
              className="bg-background"
            />
          </div>
          <div>
            <Label className="text-sm">ACS URL</Label>
            <Input
              value={`${window.location.origin}/api/v1/sso/saml/acs`}
              readOnly
              className="bg-background mt-1"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownloadMetadata}
          >
            <Download className="size-4 mr-2" />
            Download SP Metadata
          </Button>
        </div>
      )}

      {/* Basic Settings */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="display_name">Display Name</Label>
          <Input
            id="display_name"
            {...register('display_name')}
            placeholder="Company SSO"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="saml_sso_url">
            SSO URL (IdP Login URL) <span className="text-destructive">*</span>
          </Label>
          <Input
            id="saml_sso_url"
            {...register('saml_sso_url', { required: 'SSO URL is required' })}
            placeholder="https://idp.example.com/sso/saml"
            className="mt-1"
          />
          {errors.saml_sso_url && (
            <p className="text-sm text-destructive mt-1">{errors.saml_sso_url.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="saml_slo_url">SLO URL (Single Logout URL)</Label>
          <Input
            id="saml_slo_url"
            {...register('saml_slo_url')}
            placeholder="https://idp.example.com/slo/saml"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="saml_certificate">
            IdP Certificate (X.509) <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="saml_certificate"
            {...register('saml_certificate', { required: 'Certificate is required' })}
            placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
            rows={6}
            className="mt-1 font-mono text-sm"
          />
          {errors.saml_certificate && (
            <p className="text-sm text-destructive mt-1">{errors.saml_certificate.message}</p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Paste the X.509 certificate from your identity provider
          </p>
        </div>

        <div>
          <Label htmlFor="saml_name_id_format">Name ID Format</Label>
          <Select
            value={watch('saml_name_id_format')}
            onValueChange={(value) => setValue('saml_name_id_format', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NAME_ID_FORMATS.map((format) => (
                <SelectItem key={format.value} value={format.value}>
                  {format.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Advanced Settings */}
      <Accordion type="single" collapsible>
        <AccordionItem value="attribute-mapping">
          <AccordionTrigger>Attribute Mapping</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Map SAML assertion attributes to user profile fields
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email Attribute</Label>
                  <Input
                    value={watch('saml_attribute_mapping')?.email || ''}
                    onChange={(e) => setValue('saml_attribute_mapping', {
                      ...watch('saml_attribute_mapping'),
                      email: e.target.value,
                    })}
                    placeholder="email"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>First Name Attribute</Label>
                  <Input
                    value={watch('saml_attribute_mapping')?.firstName || ''}
                    onChange={(e) => setValue('saml_attribute_mapping', {
                      ...watch('saml_attribute_mapping'),
                      firstName: e.target.value,
                    })}
                    placeholder="firstName"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Last Name Attribute</Label>
                  <Input
                    value={watch('saml_attribute_mapping')?.lastName || ''}
                    onChange={(e) => setValue('saml_attribute_mapping', {
                      ...watch('saml_attribute_mapping'),
                      lastName: e.target.value,
                    })}
                    placeholder="lastName"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Groups Attribute</Label>
                  <Input
                    value={watch('saml_attribute_mapping')?.groups || ''}
                    onChange={(e) => setValue('saml_attribute_mapping', {
                      ...watch('saml_attribute_mapping'),
                      groups: e.target.value,
                    })}
                    placeholder="groups"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="provisioning">
          <AccordionTrigger>User Provisioning</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-provision Users</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically create accounts for new SSO users
                  </p>
                </div>
                <Switch
                  checked={watch('auto_provision_users')}
                  onCheckedChange={(checked) => setValue('auto_provision_users', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>JIT Provisioning</Label>
                  <p className="text-sm text-muted-foreground">
                    Update user attributes on each login
                  </p>
                </div>
                <Switch
                  checked={watch('jit_provisioning')}
                  onCheckedChange={(checked) => setValue('jit_provisioning', checked)}
                />
              </div>

              <div>
                <Label>Default Role</Label>
                <Select
                  value={watch('default_role')}
                  onValueChange={(value) => setValue('default_role', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={ssoLoading || (!isDirty && !!config)}>
          {ssoLoading ? 'Saving...' : config ? 'Update Configuration' : 'Save Configuration'}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
